/**
 * Transforms between the metrics endpoint's wire shape and what the model
 * metrics page draws (TRA-1461). Pure, so the cards, their headline numbers
 * and their empty states are testable without a DOM.
 *
 * The wire types are GENERATED from the FastAPI schema (orval). Every number
 * here is the gateway's: counts and token sums are exact, the percentiles
 * are read off its merged per-cell histograms (within a few percent of the
 * exact value), tokens per second is the bucket's two generation sums
 * divided, and requests in flight is the api's estimate by the TRA-1437
 * formula (request count times mean latency over the bucket length). The
 * window's headline percentiles are weighted means of the buckets' (see
 * `weightedMean`), not true window percentiles.
 *
 * The axis tick formatters live here too: a tick is short ("300M",
 * "1.5 s", "70%") so the axis never clips, while the readouts and legends
 * keep the exact value.
 */

import type {
  ErrorMessageRow,
  MetricsPoint,
  ModelMetricsResponse,
  ModelMetricsResponseBucket,
} from '@/api/generated/model'
import { formatCompact, weightedMean } from '@/components/models/home/contracts'
import { HOUR_MS } from '@/components/models/home/replicaPan'
import type { TimeRange } from '@/components/models/home/replicaZoom'
import { formatTime } from '@/components/models/home/scalingWindow'

export type { ErrorMessageRow, MetricsPoint, ModelMetricsResponse }

/** The four lines a percentile chart draws; null where nothing was measured. */
export interface Percentiles {
  p50: number | null
  p90: number | null
  p95: number | null
  p99: number | null
}

export interface MetricsBucket {
  /** The bucket's start, ms since the epoch; the charts' x value. */
  epoch: number
  /** The readout and tick label, in the reader's zone. */
  label: string
  requests: number
  /** Requests over the bucket's length. */
  requestsPerMinute: number
  status2xx: number
  status4xx: number
  status5xx: number
  statusOther: number
  latency: Percentiles
  ttft: Percentiles
  /** Output tokens per second after the first one; null when nothing was streamed. */
  tokensPerSecond: number | null
  promptTokens: number
  cachedPromptTokens: number
  completionTokens: number
  /** Average prompt tokens per request; null when the bucket had no requests. */
  promptPerRequest: number | null
  /** Average output tokens per request; null when the bucket had no requests. */
  completionPerRequest: number | null
  /** Percent of the bucket's prompt tokens read from the provider's cache; null without prompt tokens. */
  cacheRate: number | null
  errors: number
  /** Percent of the bucket's requests that failed; null when it had none. */
  errorRate: number | null
  /** Mean requests in flight at once (the api's TRA-1437 estimate); null when the load is unknown. */
  inFlight: number | null
  /** Prompt tokens over the bucket's length in seconds. */
  promptTokensPerSecond: number
}

/** The window's numbers: each card's headline and its legend values. */
export interface MetricsSummary {
  requestsPerMinute: number
  status2xx: number
  status4xx: number
  status5xx: number
  statusOther: number
  /** Request-weighted means of the buckets' latency percentiles. */
  latency: Percentiles
  /** Streamed-request-weighted means of the buckets' time to first token percentiles. */
  ttft: Percentiles
  tokensPerSecond: number | null
  promptTokens: number
  completionTokens: number
  promptPerRequest: number | null
  completionPerRequest: number | null
  cacheRate: number | null
  errorRate: number | null
  /** The buckets' mean requests in flight averaged; null when every bucket's load is unknown. */
  inFlight: number | null
  /** Prompt tokens over the window's length in seconds. */
  promptTokensPerSecond: number
}

export interface MetricsView {
  hours: number
  /** How long each bucket is: a drag across the charts ends at the end of its last bucket. */
  bucketMs: number
  buckets: MetricsBucket[]
  requests: number
  errors: number
  /** Some bucket measured a time to first token. */
  hasTtft: boolean
  /** Some bucket measured a generation rate. */
  hasGeneration: boolean
  /** Some bucket carried prompt tokens, so a cache rate exists. */
  hasPrompts: boolean
  summary: MetricsSummary
}

const BUCKET_MINUTES: Record<ModelMetricsResponseBucket, number> = {
  '15m': 15,
  '30m': 30,
  '1h': 60,
  '8h': 480,
}

/** `part` as a percentage of `whole`; null when there is no whole. */
export function percentOf(part: number, whole: number): number | null {
  return whole === 0 ? null : (part / whole) * 100
}

/** `total` per `count`; null when there is nothing to divide by. */
export function perUnit(total: number, count: number): number | null {
  return count === 0 ? null : total / count
}

function latencyOf(point: MetricsPoint): Percentiles {
  return {
    p50: point.p50_latency_ms,
    p90: point.p90_latency_ms,
    p95: point.p95_latency_ms,
    p99: point.p99_latency_ms,
  }
}

function ttftOf(point: MetricsPoint): Percentiles {
  return {
    p50: point.p50_ttft_ms,
    p90: point.p90_ttft_ms,
    p95: point.p95_ttft_ms,
    p99: point.p99_ttft_ms,
  }
}

function tokensPerSecond(generationTokens: number, generationMs: number): number | null {
  if (generationMs === 0) return null
  return (generationTokens * 1000) / generationMs
}

function toBucket(point: MetricsPoint, hours: number, bucketMinutes: number): MetricsBucket {
  const epoch = new Date(point.bucket_start).getTime()
  return {
    epoch,
    label: formatTime(epoch, hours),
    requests: point.request_count,
    requestsPerMinute: point.request_count / bucketMinutes,
    status2xx: point.status_2xx,
    status4xx: point.status_4xx,
    status5xx: point.status_5xx,
    statusOther: point.status_other,
    latency: latencyOf(point),
    ttft: ttftOf(point),
    tokensPerSecond: tokensPerSecond(point.generation_tokens, point.generation_ms),
    promptTokens: point.prompt_tokens,
    cachedPromptTokens: point.cached_prompt_tokens,
    completionTokens: point.completion_tokens,
    promptPerRequest: perUnit(point.prompt_tokens, point.request_count),
    completionPerRequest: perUnit(point.completion_tokens, point.request_count),
    cacheRate: percentOf(point.cached_prompt_tokens, point.prompt_tokens),
    errors: point.error_count,
    errorRate: percentOf(point.error_count, point.request_count),
    inFlight: point.in_flight,
    promptTokensPerSecond: point.prompt_tokens / (bucketMinutes * 60),
  }
}

function sum(points: MetricsPoint[], pick: (p: MetricsPoint) => number): number {
  return points.reduce((total, point) => total + pick(point), 0)
}

const byRequests = (p: MetricsPoint): number => p.request_count
const byStreamed = (p: MetricsPoint): number => p.streaming_request_count

/** A weighted mean, or null when no bucket carried the weight. */
function meanOrNull(
  points: MetricsPoint[],
  weight: (p: MetricsPoint) => number,
  pick: (p: MetricsPoint) => number | null,
): number | null {
  const measured = points.some((p) => weight(p) > 0 && pick(p) !== null)
  return measured ? weightedMean(points, weight, pick) : null
}

function windowPercentiles(
  points: MetricsPoint[],
  weight: (p: MetricsPoint) => number,
  pick: (p: MetricsPoint) => Percentiles,
): Percentiles {
  const of = (key: keyof Percentiles): number | null =>
    meanOrNull(points, weight, (p) => pick(p)[key])
  return { p50: of('p50'), p90: of('p90'), p95: of('p95'), p99: of('p99') }
}

/**
 * The window's mean requests in flight: the buckets' means averaged, since
 * every bucket is the same length. A bucket whose load is unknown (requests
 * but no mean latency) is left out. Null when no bucket WITH requests has a
 * known load: the quiet buckets' zeros are real, but averaging only those
 * would report an idle window for traffic nobody measured (Devin on #1118).
 */
function meanInFlight(points: MetricsPoint[]): number | null {
  const measured = points.some((p) => p.request_count > 0 && p.in_flight !== null)
  if (!measured) return null
  const known = points.flatMap((p) => (p.in_flight === null ? [] : [p.in_flight]))
  return known.reduce((total, value) => total + value, 0) / known.length
}

function toSummary(points: MetricsPoint[], hours: number): MetricsSummary {
  const requests = sum(points, byRequests)
  const promptTokens = sum(points, (p) => p.prompt_tokens)
  const completionTokens = sum(points, (p) => p.completion_tokens)
  return {
    requestsPerMinute: requests / (hours * 60),
    status2xx: sum(points, (p) => p.status_2xx),
    status4xx: sum(points, (p) => p.status_4xx),
    status5xx: sum(points, (p) => p.status_5xx),
    statusOther: sum(points, (p) => p.status_other),
    latency: windowPercentiles(points, byRequests, latencyOf),
    ttft: windowPercentiles(points, byStreamed, ttftOf),
    tokensPerSecond: tokensPerSecond(
      sum(points, (p) => p.generation_tokens),
      sum(points, (p) => p.generation_ms),
    ),
    promptTokens,
    completionTokens,
    promptPerRequest: perUnit(promptTokens, requests),
    completionPerRequest: perUnit(completionTokens, requests),
    cacheRate: percentOf(
      sum(points, (p) => p.cached_prompt_tokens),
      promptTokens,
    ),
    errorRate: percentOf(sum(points, (p) => p.error_count), requests),
    inFlight: meanInFlight(points),
    promptTokensPerSecond: promptTokens / (hours * 3600),
  }
}

/** The points whose bucket starts inside the stretch. */
function pointsIn(points: MetricsPoint[], shown: TimeRange): MetricsPoint[] {
  return points.filter((point) => {
    const epoch = new Date(point.bucket_start).getTime()
    return epoch >= shown.start && epoch < shown.end
  })
}

/**
 * `shown` is the page's zoom (TRA-1592): the view then keeps the buckets
 * inside it, summed and labelled as a window of its length, and reads
 * nothing; a view of the whole response without one.
 */
export function toMetricsView(response: ModelMetricsResponse, shown: TimeRange | null = null): MetricsView {
  const bucketMinutes = BUCKET_MINUTES[response.bucket]
  const hours = shown === null ? response.hours : (shown.end - shown.start) / HOUR_MS
  const points = shown === null ? response.points : pointsIn(response.points, shown)
  const buckets = points.map((point) => toBucket(point, hours, bucketMinutes))
  return {
    hours,
    bucketMs: bucketMinutes * 60 * 1000,
    buckets,
    requests: sum(points, byRequests),
    errors: sum(points, (p) => p.error_count),
    hasTtft: buckets.some((bucket) => bucket.ttft.p50 !== null),
    hasGeneration: buckets.some((bucket) => bucket.tokensPerSecond !== null),
    hasPrompts: buckets.some((bucket) => bucket.promptTokens > 0),
    summary: toSummary(points, hours),
  }
}

/** What the Errors card's message list shows. */
export interface ErrorList {
  /** The stretch's top error messages; null while they are still being read. */
  errors: ErrorMessageRow[] | null
  /** True when the list could not be read: the gateway did not answer it in time, or the zoomed stretch's read failed. */
  errorsUnavailable: boolean
}

/**
 * The Errors card's list: the loaded window's without a zoom. The list is
 * counted over a whole window, so a zoom cannot cut it the way the chart
 * is cut (TRA-1592): zoomed, it is the zoomed stretch's own read, none
 * while that read runs, and unavailable when it failed, so the card never
 * says "Loading" for a read that is not coming.
 */
export function errorList(
  loaded: ModelMetricsResponse,
  zoom: TimeRange | null,
  sharp: ModelMetricsResponse | undefined,
  sharpFailed: boolean,
): ErrorList {
  const source = zoom === null ? loaded : sharp
  if (source !== undefined) return { errors: source.top_errors, errorsUnavailable: source.errors_unavailable }
  return { errors: sharpFailed ? [] : null, errorsUnavailable: sharpFailed }
}

/**
 * The top of the error chart's axis: the window's worst bucket plus a fifth
 * of headroom, rounded up to a whole percent, never past 100. A fixed 0 to
 * 100 axis drew a 1% error rate as a flat line on the floor.
 */
export function errorAxisMax(buckets: MetricsBucket[]): number {
  const peak = Math.max(0, ...buckets.map((bucket) => bucket.errorRate ?? 0))
  return Math.min(100, Math.max(1, Math.ceil(peak * 1.2)))
}

/* ---------------------------------------------------------- status lines */

export interface StatusSeries {
  key: 'status2xx' | 'status4xx' | 'status5xx' | 'statusOther'
  label: string
  /** A chart-only status token (index.css); the app-wide status tokens stay with badges and banners. */
  stroke: string
}

const STATUS_2XX: StatusSeries = { key: 'status2xx', label: '2xx', stroke: 'hsl(var(--chart-2xx))' }
const STATUS_4XX: StatusSeries = { key: 'status4xx', label: '4xx', stroke: 'hsl(var(--chart-4xx))' }
const STATUS_5XX: StatusSeries = { key: 'status5xx', label: '5xx', stroke: 'hsl(var(--chart-5xx))' }
const STATUS_OTHER: StatusSeries = { key: 'statusOther', label: 'Other', stroke: 'hsl(var(--chart-other))' }

/** Legend and readout order, as the reader thinks of them. */
export const STATUS_LEGEND = [STATUS_2XX, STATUS_4XX, STATUS_5XX, STATUS_OTHER]

/** Drawn back to front, so the 4xx line lands on top. */
const STATUS_DRAW_ORDER = [STATUS_2XX, STATUS_OTHER, STATUS_5XX, STATUS_4XX]

/** The Requests card's lines, back to front; "other" stays off when the window had none. */
export function drawnStatusLines(summary: MetricsSummary): StatusSeries[] {
  return STATUS_DRAW_ORDER.filter((line) => line.key !== 'statusOther' || summary.statusOther !== 0)
}

/* ------------------------------------------------------------ formatting */

/** "12% to 80%" for a chart's alt text; the words for a window with no value. */
export function extent(values: number[], format: (value: number) => string): string {
  if (values.length === 0) return 'nothing measured'
  return `${format(Math.min(...values))} to ${format(Math.max(...values))}`
}

/** "412 ms" below a second, "1.4 s" above; a dash where nothing was measured. */
export function formatMs(ms: number | null): string {
  if (ms === null) return '—'
  if (ms >= 1000) return `${(ms / 1000).toFixed(1)} s`
  return `${Math.round(ms).toLocaleString()} ms`
}

/** "0.42%"; a dash where there was nothing to take a share of. */
export function formatPercent(rate: number | null): string {
  if (rate === null) return '—'
  return `${rate.toFixed(2)}%`
}

/** "812" for an average token count; a dash where there was nothing to average. */
export function formatTokens(tokens: number | null): string {
  if (tokens === null) return '—'
  return Math.round(tokens).toLocaleString()
}

/* ----------------------------------------------------------- axis ticks */

/** Up to `digits` decimals with the trailing zeros dropped: 1.50 is "1.5", 2.00 is "2". */
function trimmed(value: number, digits: number): string {
  return Number(value.toFixed(digits)).toString()
}

/**
 * A count tick: "45K", "300M", "1.2B" from a thousand up; "812", "2.5"
 * below. A rate axis on a quiet model sits under a hundredth (100 prompt
 * tokens over an 8-hour bucket is 0.0035 a second), so a tick there keeps
 * two significant digits instead of rounding to "0".
 */
export function formatCountTick(value: number): string {
  if (value >= 1000) return formatCompact(value)
  if (value > 0 && value < 0.01) return Number(value.toPrecision(2)).toString()
  return trimmed(value, 2)
}

/** A duration tick: "850 ms" under a second, "85 s" under two minutes, "2 min" from there. */
export function formatMsTick(ms: number): string {
  if (ms >= 120_000) return `${trimmed(ms / 60_000, 1)} min`
  if (ms >= 1000) return `${trimmed(ms / 1000, 1)} s`
  return `${Math.round(ms).toString()} ms`
}

/** A percent tick: "70%", "0.5%". */
export function formatPercentTick(rate: number): string {
  return `${trimmed(rate, 1)}%`
}

/** "Oct 5, 8:42 AM" in the reader's zone, for the error table's last sighting. */
export function formatSeen(iso: string): string {
  return new Date(iso).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}
